import { useEffect, useState } from "react";
import { toast } from "sonner";
import { BellRing } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const ROME = new Intl.DateTimeFormat("en-US", { timeZone: "Europe/Rome", hourCycle: "h23", year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit" });
const romeOffset = (ts) => {
  const p = Object.fromEntries(ROME.formatToParts(new Date(ts)).map((x) => [x.type, x.value]));
  return Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute) - Math.floor(ts / 60000) * 60000;
};
const releaseTs = (date) => {
  const [y, m, d] = date.split("-").map(Number);
  const utc = Date.UTC(y, m - 1, d);
  return utc - romeOffset(utc);
};
const left = (date) => Math.max(0, releaseTs(date) - Date.now());

export const ReleaseCountdown = ({ date }) => {
  const [ms, setMs] = useState(() => left(date));
  useEffect(() => {
    const t = setInterval(() => setMs(left(date)), 1000);
    return () => clearInterval(t);
  }, [date]);
  if (ms <= 0) return <p className="mt-2 font-semibold text-emerald-700" data-testid="countdown-released">Uscito! Le spedizioni sono in partenza.</p>;
  const s = Math.floor(ms / 1000);
  const parts = [[Math.floor(s / 86400), "giorni"], [Math.floor((s % 86400) / 3600), "ore"], [Math.floor((s % 3600) / 60), "min"], [s % 60, "sec"]];
  return (
    <div className="mt-3" data-testid="release-countdown">
      <p className="text-xs uppercase tracking-[0.2em] text-stone-500">Manca all'uscita</p>
      <div className="mt-1 grid max-w-xs grid-cols-4 gap-2">
        {parts.map(([v, l]) => (
          <div key={l} className="rounded-lg border-2 border-black bg-white py-1.5 text-center">
            <p className="font-display text-xl tabular-nums" data-testid={`countdown-${l}`}>{String(v).padStart(2, "0")}</p>
            <p className="text-[10px] uppercase text-stone-500">{l}</p>
          </div>
        ))}
      </div>
    </div>
  );
};

export const WaitlistForm = ({ productId }) => {
  const { user } = useAuth();
  const [email, setEmail] = useState(user?.email || "");
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post(`/products/${productId}/waitlist`, { email, lang: localStorage.getItem("cs_lang") || "it" });
      setDone(true);
      toast.success("Sei in lista d'attesa: ti scriviamo appena si libera un pezzo");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };
  if (done) return <p className="mt-3 font-semibold text-emerald-700" data-testid="waitlist-done">Iscrizione confermata: riceverai un'email appena si libera un pezzo.</p>;
  return (
    <form onSubmit={submit} className="mt-3" data-testid="waitlist-form">
      <p className="flex items-center gap-1.5 font-semibold"><BellRing className="h-4 w-4" />Avvisami se si libera un pezzo</p>
      <div className="mt-2 flex gap-2">
        <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="La tua email" className="h-10 min-w-0 flex-1 rounded-full border border-black/15 bg-white px-4 text-sm outline-none focus:border-amber-500" data-testid="waitlist-email" />
        <button disabled={busy} className="rounded-full bg-black px-4 text-sm font-semibold text-white disabled:opacity-60" data-testid="waitlist-submit">{busy ? "..." : "Iscrivimi"}</button>
      </div>
    </form>
  );
};

export const AdminWaitlist = () => {
  const [rows, setRows] = useState([]);
  useEffect(() => {
    api.get("/admin/waitlist").then((r) => setRows(r.data));
  }, []);
  return (
    <section data-testid="admin-waitlist">
      <h3 className="font-display text-xl">Lista d'attesa ({rows.filter((r) => !r.notified).length} in attesa)</h3>
      <div className="mt-3 space-y-2">
        {rows.length === 0 && <p className="text-sm text-stone-500">Nessun iscritto.</p>}
        {rows.map((w) => (
          <div key={w.id} className="flex flex-wrap items-center gap-3 rounded-xl border border-black/10 bg-white p-3 text-sm" data-testid={`waitlist-row-${w.id}`}>
            <span className="font-semibold">{w.product_name}</span>
            <span className="text-stone-600">{w.email}</span>
            <span className={`ml-auto rounded px-2 text-xs ${w.notified ? "bg-emerald-500/15 text-emerald-700" : "bg-amber-500/15 text-amber-700"}`}>{w.notified ? "avvisato" : "in attesa"}</span>
          </div>
        ))}
      </div>
    </section>
  );
};
